import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import {
    getAllIssueQueue,
    confirmIssueQueueItem,
    confirmStageIssueQueue,
    type IssueQueueItem,
    type IssueQueueSummary,
    type IssueBatchPick,
} from '@/api/issueQueue.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import IssueBatchModal from './IssueBatchModal';

export default function IssueQueuePage() {
    const toast = useToast();
    const { selectedWarehouseId } = useWarehouseStore();
    const [searchParams] = useSearchParams();

    const [items, setItems] = useState<IssueQueueItem[]>([]);
    const [summary, setSummary] = useState<IssueQueueSummary>({
        total_items: 0,
        pending_count: 0,
        issued_count: 0,
        shortage_count: 0,
    });
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<'PENDING' | 'PARTIAL' | 'ISSUED' | 'ALL'>('PENDING');
    const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [processingStageKey, setProcessingStageKey] = useState<string | null>(null);
    // Every issue goes through the issue screen: quantity (partial allowed) and, for batch-tracked
    // material, the batches
    type IssueResult = { quantities: Record<string, number>; picks: Record<string, IssueBatchPick[]> };
    const [batchPrompt, setBatchPrompt] = useState<{
        lines: IssueQueueItem[];
        run: (result: IssueResult) => Promise<void>;
    } | null>(null);

    const fetchQueue = async (overrideSearch?: string) => {
        setLoading(true);
        try {
            const queryToUse = overrideSearch !== undefined ? overrideSearch : searchQuery;
            const res = await getAllIssueQueue({
                status: statusFilter,
                warehouse_id: selectedWarehouseId && selectedWarehouseId !== 'ALL' ? selectedWarehouseId : undefined,
                search: queryToUse || undefined,
            });

            if (res.success) {
                setItems(res.data || []);
                if (res.summary) {
                    setSummary(res.summary);
                }
            } else {
                toast.error('Failed to load issue queue');
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to load issue queue');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const urlSearch = searchParams.get('search') || '';
        if (urlSearch && urlSearch !== searchQuery) {
            setSearchQuery(urlSearch);
            fetchQueue(urlSearch);
        } else {
            fetchQueue();
        }
    }, [statusFilter, selectedWarehouseId, searchParams]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        fetchQueue();
    };

    // Confirm individual item
    const handleConfirmItem = async (item: IssueQueueItem, result?: IssueResult) => {
        if (item.is_locked) {
            toast.warning(`Stage ${item.stage_number} is locked. Preceding stage QC must pass first.`);
            return;
        }
        if (!result) {
            setBatchPrompt({ lines: [item], run: (r) => handleConfirmItem(item, r) });
            return;
        }

        setProcessingId(item.id);
        try {
            const res = await confirmIssueQueueItem(item.prd_id, item.id, result.picks[item.id], result.quantities[item.id]);
            if (res.success) {
                toast.success(res.message || `Issued: ${item.item_name} (${item.required_qty} ${item.unit}) — COA Posted!`);
                setBatchPrompt(null);
                await fetchQueue();
            } else {
                toast.error(res.message || 'Failed to issue material');
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || 'Error confirming issue');
        } finally {
            setProcessingId(null);
        }
    };

    // Confirm all materials of a stage together (Rule 6)
    const handleConfirmStage = async (
        prdId: string, stageNumber: number, stageName: string, result?: IssueResult,
    ) => {
        const stageLines = items.filter((i) => i.prd_id === prdId && i.stage_number === stageNumber && ['PENDING', 'PARTIAL'].includes(i.status));
        if (!result) {
            setBatchPrompt({ lines: stageLines, run: (r) => handleConfirmStage(prdId, stageNumber, stageName, r) });
            return;
        }
        const stageKey = `${prdId}-${stageNumber}`;
        setProcessingStageKey(stageKey);
        try {
            const res = await confirmStageIssueQueue(prdId, stageNumber, result.picks, result.quantities);
            if (res.success) {
                toast.success(res.message || `Stage ${stageNumber} (${stageName}) materials confirmed — COA Posted!`);
                setBatchPrompt(null);
                await fetchQueue();
            } else {
                toast.error(res.message || 'Failed to confirm stage materials');
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || 'Error confirming stage issue');
        } finally {
            setProcessingStageKey(null);
        }
    };

    // Group items by PRD ➔ Stage
    const groupedByPrd = useMemo(() => {
        const prdMap = new Map<string, {
            prdId: string;
            poNumber: string;
            productName: string;
            plannedQty: number;
            warehouseName?: string;
            stages: Map<number, {
                stageNumber: number;
                stageName: string;
                woNumber?: string | null;
                isLocked: boolean;
                items: IssueQueueItem[];
            }>;
        }>();

        for (const item of items) {
            if (!prdMap.has(item.prd_id)) {
                prdMap.set(item.prd_id, {
                    prdId: item.prd_id,
                    poNumber: item.po_number || 'PRD',
                    productName: item.product_name || 'Production Product',
                    plannedQty: item.po_planned_qty || 1,
                    warehouseName: item.warehouse_name || undefined,
                    stages: new Map(),
                });
            }

            const prd = prdMap.get(item.prd_id)!;
            const stageNum = item.stage_number || 1;

            if (!prd.stages.has(stageNum)) {
                prd.stages.set(stageNum, {
                    stageNumber: stageNum,
                    stageName: item.stage_name || `Stage ${stageNum}`,
                    woNumber: item.wo_number || null,
                    isLocked: Boolean(item.is_locked),
                    items: [],
                });
            }

            prd.stages.get(stageNum)!.items.push(item);
        }

        return Array.from(prdMap.values());
    }, [items]);

    return (
        <AppLayout>
            <div className="space-y-6 max-w-7xl mx-auto pb-16">
                {/* ── Breadcrumb & Title ── */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <span>Manufacturing</span>
                            <i className="ri-arrow-right-s-line" />
                            <span className="text-indigo-600 font-medium">Material Issue Queue</span>
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2.5">
                            <i className="ri-inbox-unarchive-line text-indigo-600" />
                            Store Keeper — Issue Queue
                        </h1>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Stage-wise material dispatch workbench. Physical pick confirmation triggers live inventory & WIP accounting.
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={fetchQueue}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
                        >
                            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* ── KPI Summary Cards ── */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Pending Issues */}
                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Pending Items
                            </span>
                            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                                <i className="ri-time-line text-base" />
                            </div>
                        </div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-slate-900">{summary.pending_count}</span>
                            <span className="text-xs text-amber-600 font-medium">Awaiting pick</span>
                        </div>
                    </div>

                    {/* Shortages */}
                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Stock Shortages
                            </span>
                            <div className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                                <i className="ri-error-warning-line text-base" />
                            </div>
                        </div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-red-600">{summary.shortage_count}</span>
                            <span className="text-xs text-red-500 font-medium">Low warehouse stock</span>
                        </div>
                    </div>

                    {/* Issued Today */}
                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Issued Items
                            </span>
                            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                                <i className="ri-checkbox-circle-line text-base" />
                            </div>
                        </div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-emerald-700">{summary.issued_count}</span>
                            <span className="text-xs text-emerald-600 font-medium">Dispatched to floor</span>
                        </div>
                    </div>

                    {/* Total Tracked */}
                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Total Tracked
                            </span>
                            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                <i className="ri-archive-line text-base" />
                            </div>
                        </div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-slate-900">{summary.total_items}</span>
                            <span className="text-xs text-slate-500 font-medium">In active PRDs</span>
                        </div>
                    </div>
                </div>

                {/* ── Filters Bar ── */}
                <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs flex flex-col md:flex-row items-center justify-between gap-3">
                    {/* Status Tabs */}
                    <div className="flex items-center bg-slate-100 p-1 rounded-lg w-full md:w-auto">
                        <button
                            type="button"
                            onClick={() => setStatusFilter('PENDING')}
                            className={`flex-1 md:flex-none px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                                statusFilter === 'PENDING'
                                    ? 'bg-white text-indigo-600 shadow-xs'
                                    : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            Pending Issues ({summary.pending_count})
                        </button>
                        <button
                            type="button"
                            onClick={() => setStatusFilter('PARTIAL')}
                            className={`flex-1 md:flex-none px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                                statusFilter === 'PARTIAL'
                                    ? 'bg-white text-indigo-600 shadow-xs'
                                    : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            Partial{summary.partial_count !== undefined ? ` (${summary.partial_count})` : ''}
                        </button>
                        <button
                            type="button"
                            onClick={() => setStatusFilter('ISSUED')}
                            className={`flex-1 md:flex-none px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                                statusFilter === 'ISSUED'
                                    ? 'bg-white text-indigo-600 shadow-xs'
                                    : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            Issued History ({summary.issued_count})
                        </button>
                        <button
                            type="button"
                            onClick={() => setStatusFilter('ALL')}
                            className={`flex-1 md:flex-none px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                                statusFilter === 'ALL'
                                    ? 'bg-white text-indigo-600 shadow-xs'
                                    : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            All ({summary.total_items})
                        </button>
                    </div>

                    {/* Search */}
                    <form onSubmit={handleSearchSubmit} className="relative w-full md:w-72">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search PRD #, item name, code..."
                            className="w-full h-9 pl-9 pr-8 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchQuery('');
                                    fetchQueue();
                                }}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                                <i className="ri-close-line text-sm" />
                            </button>
                        )}
                    </form>
                </div>

                {/* ── Main Queue Content (Grouped by PRD ➔ Stage) ── */}
                {loading ? (
                    <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-xs">
                        <i className="ri-loader-4-line animate-spin text-3xl text-indigo-600 block mb-3" />
                        <p className="text-sm font-semibold text-slate-700">Loading Issue Queue...</p>
                        <p className="text-xs text-slate-400 mt-1">Fetching pending material releases from shop floor orders</p>
                    </div>
                ) : groupedByPrd.length === 0 ? (
                    <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-xs">
                        <div className="w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-500 flex items-center justify-center mx-auto mb-4">
                            <i className="ri-checkbox-circle-line text-3xl" />
                        </div>
                        <h3 className="text-base font-bold text-slate-800">No Material Issues In Queue</h3>
                        <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                            {statusFilter === 'PENDING'
                                ? 'All required stage materials have been issued. No work orders are currently waiting for stock.'
                                : 'No records found matching your current filter criteria.'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {groupedByPrd.map((prd) => (
                            <div key={prd.prdId} className="bg-white border border-slate-200 rounded-2xl shadow-xs overflow-hidden">
                                {/* PRD Header */}
                                <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-indigo-50/20 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-xs">
                                            PRD
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h2 className="text-base font-bold text-slate-900">{prd.poNumber}</h2>
                                                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                                                    Qty: {prd.plannedQty} units
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500">
                                                Product: <strong className="text-slate-700">{prd.productName}</strong>
                                                {prd.warehouseName && ` • Warehouse: ${prd.warehouseName}`}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Stages Accordion / Subgroups */}
                                <div className="divide-y divide-slate-100">
                                    {Array.from(prd.stages.values()).map((st) => {
                                        const stageKey = `${prd.prdId}-${st.stageNumber}`;
                                        const isStageProcessing = processingStageKey === stageKey;
                                        const pendingItemsInStage = st.items.filter((i) => ['PENDING', 'PARTIAL'].includes(i.status));
                                        const hasPending = pendingItemsInStage.length > 0;

                                        return (
                                            <div key={st.stageNumber} className="p-5 space-y-4">
                                                {/* Stage Header */}
                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/80 p-3.5 rounded-xl border border-slate-200/80">
                                                    <div className="flex items-center gap-2.5">
                                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold">
                                                            {st.stageNumber}
                                                        </span>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <h3 className="text-sm font-bold text-slate-800">
                                                                    {st.stageName}
                                                                </h3>
                                                                {st.woNumber && (
                                                                    <span className="font-mono text-xs text-slate-500">
                                                                        ({st.woNumber})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-[11px] text-slate-500">
                                                                {st.items.length} materials tracked for this operation stage
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2.5">
                                                        {st.isLocked ? (
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                                                                <i className="ri-lock-line" />
                                                                Locked (Previous QC Pending)
                                                            </span>
                                                        ) : hasPending ? (
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 animate-pulse">
                                                                <i className="ri-time-line" />
                                                                Active — Ready for Issue
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                                <i className="ri-checkbox-circle-line" />
                                                                Fully Issued
                                                            </span>
                                                        )}

                                                        {/* Confirm Entire Stage (Rule 6: all materials issued together) */}
                                                        {hasPending && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleConfirmStage(prd.prdId, st.stageNumber, st.stageName)}
                                                                disabled={st.isLocked || isStageProcessing}
                                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                                    st.isLocked
                                                                        ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                                                                        : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-xs cursor-pointer'
                                                                }`}
                                                                title={st.isLocked ? 'Cannot issue locked stage materials' : 'Issue all materials for this stage together'}
                                                            >
                                                                {isStageProcessing ? (
                                                                    <>
                                                                        <i className="ri-loader-4-line animate-spin" />
                                                                        Issuing Stage...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="ri-check-double-line text-sm" />
                                                                        Confirm Entire Stage ({pendingItemsInStage.length})
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Materials Table for this stage */}
                                                <div className="overflow-x-auto rounded-xl border border-slate-200">
                                                    <table className="w-full text-left border-collapse">
                                                        <thead>
                                                            <tr className="bg-slate-50 border-b border-slate-200">
                                                                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                                                    Material / Component
                                                                </th>
                                                                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                                                    Required Qty
                                                                </th>
                                                                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                                                    Warehouse Stock
                                                                </th>
                                                                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                                                    Status
                                                                </th>
                                                                <th className="px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide text-right">
                                                                    Action
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100 text-xs">
                                                            {st.items.map((item) => {
                                                                // What the order can take now against what is still to issue
                                                                const available = Number(item.free_qty ?? item.stock_available ?? 0);
                                                                const required = Number(item.pending_qty ?? item.required_qty ?? 0);
                                                                const isShortage = available < required;
                                                                const isPartial = item.status === 'PARTIAL';
                                                                const isItemProcessing = processingId === item.id;
                                                                const isIssued = item.status === 'ISSUED';

                                                                return (
                                                                    <tr key={item.id} className="hover:bg-slate-50/60 transition-colors">
                                                                        {/* Name & Code */}
                                                                        <td className="px-4 py-3">
                                                                            <div className="font-semibold text-slate-800">
                                                                                {item.item_name}
                                                                            </div>
                                                                            <div className="text-[11px] text-slate-400 font-mono">
                                                                                {item.item_code || item.item_id}
                                                                                {item.notes && ` • ${item.notes}`}
                                                                            </div>
                                                                        </td>

                                                                        {/* Required Qty */}
                                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                                            <span className="font-bold text-slate-800 text-sm">
                                                                                {required} {item.unit}
                                                                            </span>
                                                                        </td>

                                                                        {/* Stock in Warehouse */}
                                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                                            <div className="flex items-center gap-1.5">
                                                                                <span className={`font-semibold ${isShortage ? 'text-red-600' : 'text-emerald-700'}`}>
                                                                                    {available} {item.unit}
                                                                                </span>
                                                                                {isIssued ? null : isShortage ? (
                                                                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">
                                                                                        Shortage: -{(required - available).toFixed(2)}
                                                                                    </span>
                                                                                ) : isPartial ? (
                                                                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700" title="Enough stock has arrived for what is pending">
                                                                                        Stock available — issue rest
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700">
                                                                                        Available
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </td>

                                                                        {/* Status */}
                                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                                            {isIssued ? (
                                                                                <div>
                                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                                                                        <i className="ri-checkbox-circle-fill text-emerald-600" />
                                                                                        ISSUED
                                                                                    </span>
                                                                                    {item.issued_at && (
                                                                                        <p className="text-[10px] text-slate-400 mt-0.5">
                                                                                            {new Date(item.issued_at).toLocaleDateString()} {new Date(item.issued_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                                        </p>
                                                                                    )}
                                                                                    {(item.batch_allocations?.length ?? 0) > 0 && (
                                                                                        <p className="text-[10px] text-slate-500 mt-0.5" title="Batches this material was issued from">
                                                                                            <i className="ri-stack-line" /> {item.batch_allocations!.map((b) => `${b.batchNumber} (${b.qty})`).join(', ')}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            ) : isPartial ? (
                                                                                <div>
                                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800">
                                                                                        <i className="ri-contrast-2-line text-amber-600" />
                                                                                        PARTIAL
                                                                                    </span>
                                                                                    <p className="text-[10px] text-slate-500 mt-0.5">
                                                                                        {Number(item.issued_qty)} issued · {Number(item.pending_qty ?? 0)} {item.unit} pending
                                                                                    </p>
                                                                                </div>
                                                                            ) : st.isLocked ? (
                                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-500">
                                                                                    <i className="ri-lock-line" />
                                                                                    LOCKED
                                                                                </span>
                                                                            ) : (
                                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800">
                                                                                    <i className="ri-time-line text-amber-600" />
                                                                                    PENDING
                                                                                </span>
                                                                            )}
                                                                        </td>

                                                                        {/* Action Button */}
                                                                        <td className="px-4 py-3 whitespace-nowrap text-right">
                                                                            {isIssued ? (
                                                                                <span className="text-slate-400 text-xs font-medium">
                                                                                    Dispatched
                                                                                </span>
                                                                            ) : (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleConfirmItem(item)}
                                                                                    disabled={st.isLocked || isItemProcessing}
                                                                                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                                                                        st.isLocked
                                                                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                                                                                            : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-xs cursor-pointer'
                                                                                    }`}
                                                                                    title={st.isLocked ? 'Stage is locked. Complete previous QC pass first.' : 'Confirm physical pick & post COA'}
                                                                                >
                                                                                    {isItemProcessing ? (
                                                                                        <i className="ri-loader-4-line animate-spin" />
                                                                                    ) : st.isLocked ? (
                                                                                        <i className="ri-lock-line" />
                                                                                    ) : (
                                                                                        <i className="ri-check-line" />
                                                                                    )}
                                                                                    {st.isLocked ? 'Locked' : isPartial ? 'Issue Rest' : 'Confirm Issue'}
                                                                                </button>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <IssueBatchModal
                open={!!batchPrompt}
                lines={batchPrompt?.lines || []}
                busy={!!processingId || !!processingStageKey}
                onCancel={() => setBatchPrompt(null)}
                onConfirm={(result) => { void batchPrompt?.run(result); }}
            />
        </AppLayout>
    );
}
